import { formatInTimeZone } from 'date-fns-tz';
import ExcelJS from 'exceljs';
import { useCallback, useState, Dispatch, SetStateAction } from 'react';
import * as uuid from 'uuid';
import { excelNumberToDate } from '@blisspointmedia/bpm-types/dist/excelUtils';
import { greaterThanIgnoreTime, isParseable } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { MenuItem, TimelineEntry } from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import { ImportModal } from '@Components/ImportModal';
import { sheetToJson } from '@src/utils/exceljs-utils';
import { SetError } from '../../../redux/modals';

interface TruncatedEconEntry {
  promo_name: string;
  promo_discount: number | null;
  live_dates: number[];
}

interface CanonicalizedExcelEntry {
  promo_name: string;
  special_offer_name: string | null;
  percentage_discount: number | null;
  start_date: number;
  end_date: number;
}

interface BulkImportPromoEntry {
  promoName: string;
  specialOfferName: string | null;
  percentageDiscount: number | null;
  startDate: string;
  endDate: string;
  isFromEconImport: boolean;
}

interface ImportPromoDataModalProps {
  show: boolean;
  promos: MenuItem[];
  offers: MenuItem[];
  userFullName: string;
  handleClose: () => void;
  stageEntry: (input: TimelineEntry) => void;
  setPromos: (promos: MenuItem[]) => void;
  setOffers: (offers: MenuItem[]) => void;
  setEditingUnlocked: Dispatch<SetStateAction<boolean>>;
  setError: SetError;
}

const validatePromoEntry = (entry: BulkImportPromoEntry): boolean => {
  const isNotValid =
    !entry.promoName ||
    (!entry.percentageDiscount && !entry.specialOfferName && !entry.isFromEconImport) ||
    !entry.startDate ||
    !isParseable(entry.startDate) ||
    !entry.endDate ||
    !isParseable(entry.endDate) ||
    !greaterThanIgnoreTime(entry.endDate, entry.startDate, true);
  return !isNotValid;
};

const processPromoEntries = (
  userFullName: string,
  rows: CanonicalizedExcelEntry[],
  promos: MenuItem[],
  offers: MenuItem[],
  isFromEconImport: boolean
): {
  newPromoEntryMap: Record<string, BulkImportPromoEntry>;
  newPromoNameArr: MenuItem[];
  newOfferNameArr: MenuItem[];
} => {
  const newPromoEntryMap: Record<string, BulkImportPromoEntry> = {};
  const newPromoNameArr: MenuItem[] = [];
  const newOfferNameArr: MenuItem[] = [];
  const today = formatInTimeZone(new Date(), 'UTC', 'yyyy-MM-dd');
  // Every new menu item needs a distinct negative sentinel id. `-Date.now()` alone collided
  // across same-millisecond rows and between a row's promo/offer entries, breaking dedupe and
  // delete-by-id. Decrement from a per-import seed so each item is unique.
  let nextNewId = -Date.now();
  const makeNewId = () => {
    nextNewId -= 1;
    return nextNewId;
  };
  rows.forEach(row => {
    if (
      !row.promo_name &&
      !row.percentage_discount &&
      !row.special_offer_name &&
      !row.start_date &&
      !row.end_date
    ) {
      return;
    }

    const promoEntry: BulkImportPromoEntry = {
      promoName: row.promo_name.trim(),
      percentageDiscount: row.percentage_discount != null ? Number(row.percentage_discount) : null,
      specialOfferName: row.special_offer_name ? row.special_offer_name.trim() : null,
      startDate: excelNumberToDate(row.start_date),
      endDate: excelNumberToDate(row.end_date),
      isFromEconImport
    };
    const key = `${promoEntry.promoName} | ${promoEntry.percentageDiscount} | ${promoEntry.specialOfferName} | ${promoEntry.startDate} | ${promoEntry.endDate}`;
    if (!validatePromoEntry(promoEntry)) {
      throw Error(key);
    }
    newPromoEntryMap[key] = promoEntry;
    const menuItemBase = {
      updatedAt: today,
      updatedBy: userFullName,
      isDeletable: true
    };
    if (
      !promos.some(item => item.label === promoEntry.promoName) &&
      !newPromoNameArr.some(item => item.label === promoEntry.promoName)
    ) {
      newPromoNameArr.push({ ...menuItemBase, id: makeNewId(), label: promoEntry.promoName });
    }
    if (
      promoEntry.specialOfferName &&
      !offers.some(item => item.label === promoEntry.specialOfferName) &&
      !newOfferNameArr.some(item => item.label === promoEntry.specialOfferName)
    ) {
      newOfferNameArr.push({
        ...menuItemBase,
        id: makeNewId(),
        label: promoEntry.specialOfferName
      });
    }
  });
  return {
    newPromoEntryMap,
    newPromoNameArr,
    newOfferNameArr
  };
};

const PROMO_NAME_REGEXP_LITERAL = /promo_name_(\d+)/;
const PROMO_DISCOUNT_REGEXP_LITERAL = /promo_discount_(\d+)/;

// groups consecutive (day-adjacent) live dates into contiguous ranges.
const groupConsecutiveDates = (dates: number[]): number[][] => {
  const groups: number[][] = [];
  dates.forEach(date => {
    const currentGroup = groups[groups.length - 1];
    if (currentGroup && date - currentGroup[currentGroup.length - 1] === 1) {
      currentGroup.push(date);
    } else {
      groups.push([date]);
    }
  });
  return groups;
};

const toCanonicalizedExcelEntryArr = (
  truncatedPromos: TruncatedEconEntry[]
): CanonicalizedExcelEntry[] => {
  const canonicalizedArr: CanonicalizedExcelEntry[] = [];

  truncatedPromos.forEach(promo => {
    promo.live_dates.sort((a, b) => a - b);
    const consecutiveLiveDates = groupConsecutiveDates(promo.live_dates);

    consecutiveLiveDates.forEach(dateRange =>
      canonicalizedArr.push({
        promo_name: promo.promo_name,
        percentage_discount: promo.promo_discount,
        special_offer_name: null,
        start_date: dateRange[0],
        end_date: dateRange[dateRange.length - 1]
      })
    );
  });
  return canonicalizedArr;
};

// historical econometric excels utilize a different table format than our current template.
// this function transforms a historical excel into a canonicalized entry array.
const parseEconExcel = (rows: any[]): CanonicalizedExcelEntry[] => {
  // maps 'name | discount' key to array of dates the promo was live
  const promoLiveDateMap: Map<string, TruncatedEconEntry> = new Map();
  rows.forEach(row => {
    const { date } = row;
    // Econ excels are formatted promo_name_1, promo_discount_1, ...
    // Maps each numerical index to its name and discount
    const indexMap = new Map<number, { promo_name?: string; promo_discount?: number | null }>();

    Object.keys(row).forEach(col => {
      if (row[col]) {
        const promoNameMatch = col.trim().match(PROMO_NAME_REGEXP_LITERAL);
        if (promoNameMatch) {
          const index = Number.parseInt(promoNameMatch[1], 10);
          const promoName = row[col];
          indexMap.set(index, { ...indexMap.get(index), promo_name: promoName });
        }
        const promoDiscountMatch = col.trim().match(PROMO_DISCOUNT_REGEXP_LITERAL);
        if (promoDiscountMatch) {
          const index = Number.parseInt(promoDiscountMatch[1], 10);
          const percentageDiscount = Number.parseFloat(row[col]) * 100;
          if (
            Number.isNaN(percentageDiscount) ||
            percentageDiscount > 100 ||
            percentageDiscount < 0
          ) {
            throw Error(
              `${col} must be a decimal value between 0 and 1. See date: ${excelNumberToDate(date)}`
            );
          }
          indexMap.set(index, {
            ...indexMap.get(index),
            promo_discount: percentageDiscount
          });
        }
      }
    });
    // indexMap values represent the live promos for a given date
    // if key exists, append dates to live_dates array, otherwise add full entry.
    Array.from(indexMap.values())
      .filter(entry => entry.promo_name)
      .forEach(entry => {
        const key = `${entry.promo_name} | ${entry.promo_discount}`;
        const existing = promoLiveDateMap.get(key);
        if (existing) {
          promoLiveDateMap.set(key, { ...existing, live_dates: [...existing.live_dates, date] });
        } else {
          promoLiveDateMap.set(key, {
            promo_name: entry.promo_name!,
            promo_discount: entry.promo_discount ?? null,
            live_dates: [date]
          });
        }
      });
  });
  return toCanonicalizedExcelEntryArr(Array.from(promoLiveDateMap.values()));
};

export const ImportPromoDataModal = ({
  show,
  promos,
  offers,
  userFullName,
  handleClose,
  stageEntry,
  setPromos,
  setOffers,
  setEditingUnlocked,
  setError
}: ImportPromoDataModalProps) => {
  const [file, setFile] = useState<File | undefined>();

  const importPromoData = useCallback(async (): Promise<void> => {
    if (!file) {
      return;
    }
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      const worksheet = workbook.worksheets[0];
      const rows = sheetToJson(worksheet);
      let processedRows: CanonicalizedExcelEntry[];
      let isHistoricalEconImport = false;
      if (rows.length && 'date' in rows[0]) {
        isHistoricalEconImport = true;
        processedRows = parseEconExcel(rows);
      } else {
        processedRows = rows as unknown as CanonicalizedExcelEntry[];
      }

      const { newPromoEntryMap, newPromoNameArr, newOfferNameArr } = processPromoEntries(
        userFullName,
        processedRows,
        promos,
        offers,
        isHistoricalEconImport
      );

      if (Object.keys(newPromoEntryMap).length === 0) {
        throw Error(
          'No valid promo entries found. Ensure your promo data sheet is the first tab and uses the standardized column structure'
        );
      }

      Object.values(newPromoEntryMap).forEach(bulkImportPromoEntry => {
        if (bulkImportPromoEntry != null && validatePromoEntry(bulkImportPromoEntry)) {
          stageEntry({
            ...bulkImportPromoEntry,
            promoPeriodId: uuid.v4()
          });
        }
      });
      setPromos([...promos, ...newPromoNameArr]);
      setOffers([...offers, ...newOfferNameArr]);
      setEditingUnlocked(true);
    } catch (e) {
      setError({
        message: `Failed to import: ${e.message}`,
        reportError: e
      });
      throw e;
    }
  }, [
    file,
    userFullName,
    promos,
    offers,
    setPromos,
    setOffers,
    setEditingUnlocked,
    setError,
    stageEntry
  ]);

  if (!show) {
    return null;
  }

  return (
    <ImportModal
      importFunction={importPromoData}
      file={file}
      setFile={selectedFile => {
        if (!selectedFile || !selectedFile.name.endsWith('.xlsx')) {
          setError({
            message: 'File must be a .xlsx',
            reportError: new Error('File must be a .xlsx')
          });
        } else {
          setFile(selectedFile);
        }
      }}
      onClose={handleClose}
      subtitle={
        <>
          <div className='first-bullet'>
            <strong> All dates must be stores as an excel date type within uploaded files.</strong>
          </div>
          <div className='bullet'>
            <strong>XLSX Columns:</strong> promo_name, percentage_discount, special_offer_name,
            start_date, end_date.
          </div>
          <div className='bullet'>
            <strong>Historical Econometric Excels:</strong> please remove top-row instructions /
            merged cells and then upload as-is.
          </div>
        </>
      }
    />
  );
};
